'use client';

import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../../lib/constants';
import type { ProductListingProducts } from '../server/get-product-listing-products';
import type { FilterValuesType } from '../use-product-listing-filters';
import type { SortOption } from '../use-product-listing-sort-state';
import type { ProductListingPageSettings } from './product-listing-types';

export function useProductListingProducts({
  slug,
  currentPage,
  sort,
  settings,
  filterValues,
}: {
  slug: string;
  currentPage: number;
  sort: SortOption;
  settings: ProductListingPageSettings;
  filterValues?: FilterValuesType;
}) {
  return useQuery({
    queryKey: [
      QUERY_KEYS.PRODUCT_LISTING_PRODUCTS,
      slug,
      currentPage,
      sort,
      filterValues,
    ],
    queryFn: async () =>
      await getProductListingProducts({
        slug,
        currentPage,
        sort,
        settings,
        filterValues,
      }),
    placeholderData: (prevData) => prevData,
  });
}

export async function getProductListingProducts({
  slug,
  currentPage,
  sort,
  settings,
  filterValues,
}: {
  slug: string;
  currentPage: number;
  sort: SortOption;
  settings: ProductListingPageSettings;
  filterValues?: FilterValuesType;
}): Promise<ProductListingProducts> {
  const searchParams = new URLSearchParams({
    currentPage: currentPage.toString(),
    sort,
    settings: JSON.stringify(settings),
  });

  if (filterValues) {
    searchParams.append('filters', JSON.stringify(filterValues));
  }

  const response = await fetch(
    `/api/sanity/collections/${slug}/products?${searchParams}`
  );
  if (!response.ok) {
    return [];
  }
  const data = await response.json();
  return data;
}
